import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import NavigatorManageTable, {
  nameSort,
  tokenizedNameSearch,
  OverflowNameList,
  SimpleActionsMenu,
} from './NavigatorManageTable';

// Thin wrapper over the shared NavigatorManageTable for the Exercises page's
// Tags tab — mirrors ManageExercisesTable's structure.

const NameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
`;

const BULK_ACTIONS = [
  {
    key: 'deleteTags', label: 'Delete', icon: 'delete', iconColor: '#FF0505',
  },
];

const NameCell = ({ row }) => {
  const { name } = row.original;
  return <NameText title={name}>{name || 'Untitled tag'}</NameText>;
};

NameCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.shape({ name: PropTypes.string }) }).isRequired,
};

const ExercisesCell = ({ row }) => {
  const { exercises, id } = row.original;
  return <OverflowNameList items={exercises} idPrefix='tag-exercises' rowId={id} variant='pill' />;
};

ExercisesCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      exercises: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })),
    }),
  }).isRequired,
};

const TagActionsCell = ({ row }) => {
  const t = row.original;
  return <SimpleActionsMenu onEdit={() => t.onEdit(t)} onDelete={() => t.onDelete(t)} />;
};

TagActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      onEdit: PropTypes.func,
      onDelete: PropTypes.func,
    }),
  }).isRequired,
};

const ManageTagsTable = ({
  tags, isLoading, hasError, onRetry, onEditTag, onDeleteTag, onBulkDelete, clearSelectionToken,
}) => {
  const tableData = React.useMemo(
    () => (tags || []).map((t) => ({ ...t, onEdit: onEditTag, onDelete: onDeleteTag })),
    [tags, onEditTag, onDeleteTag],
  );

  const columns = React.useMemo(() => [
    {
      Header: 'Tag',
      accessor: 'name',
      sortType: nameSort,
      headerStyle: { minWidth: '280px', flex: '1' },
      cellStyle: { minWidth: '280px', flex: '1' },
      Cell: NameCell,
    },
    {
      Header: 'Exercises',
      id: 'exercises',
      accessor: (row) => (row.exercises || []).map((e) => e.name).join(' '),
      disableSortBy: true,
      headerStyle: { width: '380px' },
      cellStyle: { width: '380px' },
      Cell: ExercisesCell,
    },
    {
      Header: 'Actions',
      id: 'actions',
      sticky: 'right',
      disableSortBy: true,
      headerStyle: { width: '90px', textAlign: 'center' },
      cellStyle: { width: '90px', textAlign: 'center' },
      Cell: TagActionsCell,
    },
  ], []);

  const handleBulkAction = (key, selectedIds) => {
    if (key === 'deleteTags') onBulkDelete(selectedIds);
  };

  return (
    <NavigatorManageTable
      data={tableData}
      columns={columns}
      isLoading={isLoading}
      hasError={hasError}
      errorMessage='Something went wrong loading tags.'
      onRetry={onRetry}
      searchKeys={['name']}
      searchFn={tokenizedNameSearch}
      searchPlaceholder='Search tags'
      emptyMessage='No tags yet.'
      searchEmptyMessage='No tags match your search.'
      entityLabel='tag'
      defaultSortBy={[{ id: 'name', desc: false }]}
      bulkActions={BULK_ACTIONS}
      onBulkAction={handleBulkAction}
      clearSelectionToken={clearSelectionToken}
      virtualizeRows
    />
  );
};

ManageTagsTable.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.shape({})),
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  onRetry: PropTypes.func,
  onEditTag: PropTypes.func,
  onDeleteTag: PropTypes.func,
  onBulkDelete: PropTypes.func,
  clearSelectionToken: PropTypes.number,
};

ManageTagsTable.defaultProps = {
  tags: [],
  isLoading: false,
  hasError: false,
  onRetry: () => {},
  onEditTag: () => {},
  onDeleteTag: () => {},
  onBulkDelete: () => {},
  clearSelectionToken: 0,
};

export default ManageTagsTable;
